import { getDownload } from "../download.js";
import { createButton, startDownload } from "../utils/dom.js";

/**
 * Builds the page-download dialog and resolves the selected download.
 */
export default class DownloadDialog {
  #createDialog;

  #downloadResolver;

  #downloadStarter;

  #logger;

  #handleClose;

  constructor({
    anchor,
    track,
    info,
    i18n,
    createDialog = (documentRoot) => documentRoot.createElement("dialog"),
    downloadResolver = getDownload,
    downloadStarter = startDownload,
    logger = console,
  }) {
    this.anchor = anchor;
    this.track = track;
    this.info = info;
    this.i18n = i18n;
    this.#createDialog = createDialog;
    this.#downloadResolver = downloadResolver;
    this.#downloadStarter = downloadStarter;
    this.#logger = logger;
  }

  show(doublePages = false) {
    if (this.dialog?.open) return this.dialog;

    this.#removeDialog();
    this.dialog = this.#createDialog(this.anchor.ownerDocument);
    this.dialog.classList.add("rhea-download-dialog");
    this.anchor.after(this.dialog);

    this.dialog.append(
      this.#button(
        this.i18n.downloadAll,
        () => this.#download("all"),
        "rhea-download-all",
      ),
    );
    if (doublePages) {
      if (this.#hasDownload(1)) {
        this.dialog.append(
          this.#button(
            this.i18n.downloadLeftPage,
            () => this.#download(1),
            "rhea-download-left",
          ),
        );
      }
      if (this.#hasDownload(2)) {
        this.dialog.append(
          this.#button(
            this.i18n.downloadRightPage,
            () => this.#download(2),
            "rhea-download-right",
          ),
        );
      }
    } else if (this.#hasDownload(1)) {
      this.dialog.append(
        this.#button(
          this.i18n.downloadPage,
          () => this.#download(1),
          "rhea-download-single",
        ),
      );
    }
    this.dialog.append(
      createButton(
        this.i18n.close,
        () => this.#closeDialog(),
        ["rhea-button-small", "rhea-icon", "close"],
        undefined,
        this.anchor.ownerDocument,
      ),
    );

    this.#handleClose = () => this.#removeDialog();
    this.dialog.addEventListener("close", this.#handleClose, { once: true });
    this.dialog.showModal();
    return this.dialog;
  }

  destroy() {
    this.#removeDialog();
  }

  #button(label, callback, cssClass) {
    return createButton(
      label,
      callback,
      ["rhea-button-small", "rhea-icon", "download", cssClass],
      undefined,
      this.anchor.ownerDocument,
    );
  }

  #download(page) {
    try {
      this.#downloadStarter(
        this.#downloadResolver(page, this.track, this.info),
        this.anchor.ownerDocument,
      );
      this.#closeDialog();
      return true;
    } catch (error) {
      this.#logger.warn("RheaBookViewer: download is not available.", error);
      return false;
    }
  }

  #hasDownload(page) {
    try {
      this.#downloadResolver(page, this.track, this.info);
      return true;
    } catch {
      return false;
    }
  }

  #closeDialog() {
    const dialog = this.dialog;
    if (!dialog) return;

    dialog.close();
    if (this.dialog === dialog) this.#removeDialog();
  }

  #removeDialog() {
    if (!this.dialog) return;
    if (this.#handleClose) {
      this.dialog.removeEventListener("close", this.#handleClose);
      this.#handleClose = undefined;
    }
    this.dialog.remove();
    this.dialog = undefined;
  }
}
